import { useState, useEffect, useCallback } from "react";
import {
  Music, Radio, Activity, X,
  Download, Shield, Search, Loader2
} from "lucide-react";
import { ListenerMap } from "./ListenerMap";
import { ListenerHeatmap } from "./ListenerHeatmap";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PRO_COLORS = {
  ASCAP: { bg: "bg-blue-500/15", text: "text-blue-400", border: "border-blue-500/30" },
  BMI: { bg: "bg-green-500/15", text: "text-green-400", border: "border-green-500/30" },
  SESAC: { bg: "bg-orange-500/15", text: "text-orange-400", border: "border-orange-500/30" },
  SoundExchange: { bg: "bg-purple-500/15", text: "text-purple-400", border: "border-purple-500/30" },
  "Rights-Cleared": { bg: "bg-teal-500/15", text: "text-teal-400", border: "border-teal-500/30" },
  Unknown: { bg: "bg-red-500/10", text: "text-red-400", border: "border-red-500/20" },
};

const PROBadge = ({ pro, small }) => {
  // pro can be a string or array
  const pros = Array.isArray(pro) ? pro : [pro];
  if (pros.length === 1 && pros[0] === "Unknown") {
    const c = PRO_COLORS.Unknown;
    return <span className={`${c.bg} ${c.text} border ${c.border} font-bold rounded-full ${small ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1"}`} data-testid="pro-badge-Unknown">Unknown</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {pros.filter(p => p !== "Unknown").map(p => {
        const c = PRO_COLORS[p] || PRO_COLORS.Unknown;
        return <span key={p} className={`${c.bg} ${c.text} border ${c.border} font-bold rounded-full ${small ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1"}`} data-testid={`pro-badge-${p}`}>{p}</span>;
      })}
    </div>
  );
};

// Inline PRO Override Editor — supports multiple PROs + web lookup
const PROInlineEditor = ({ artist, currentPro, authToken, onSaved, proSource }) => {
  const [editing, setEditing] = useState(false);
  const currentPros = Array.isArray(currentPro) ? currentPro : [currentPro];
  const [selected, setSelected] = useState(currentPros.filter(p => p !== "Unknown"));
  const [saving, setSaving] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const pros = ["ASCAP", "BMI", "SESAC", "SoundExchange", "Rights-Cleared"];

  const toggle = (pro) => {
    setSelected(prev => prev.includes(pro) ? prev.filter(p => p !== pro) : [...prev, pro]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${BACKEND_URL}/api/pro/override`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ artist, pro_names: selected.length > 0 ? selected : ["Unknown"] }),
      });
      setEditing(false);
      setLookupResult(null);
      onSaved?.();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const lookupPRO = async (e) => {
    e?.stopPropagation();
    setLookingUp(true);
    setLookupResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/pro/lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ artist }),
      });
      const data = await res.json();
      setLookupResult(data);
      if (data.pro_names?.length > 0) {
        setSelected(data.pro_names);
        if (data.source === "Auto-detected") {
          onSaved?.(); // Refresh — auto-detected saves automatically
        }
      }
    } catch (e) { console.error(e); }
    finally { setLookingUp(false); }
  };

  const isUnknown = currentPros.length === 1 && currentPros[0] === "Unknown";
  const isAutoDetected = proSource === "Auto-detected";
  const isNeedsReview = proSource === "Needs Review";

  if (!editing) {
    return (
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => { setEditing(true); setSelected(currentPros.filter(p => p !== "Unknown")); }}
          className="group flex items-center gap-1.5 cursor-pointer"
          title={`Click to assign PRO for ${artist}`}
          data-testid={`pro-edit-${artist}`}
        >
          <PROBadge pro={currentPro} small />
          {isAutoDetected && (
            <span className="text-[9px] text-cyan-400/60 font-medium" title="Auto-detected via web search">auto</span>
          )}
          <span className="text-white/0 group-hover:text-purple-400 text-[10px] font-medium transition-all">
            {isUnknown ? "Assign" : "Edit"}
          </span>
        </button>
        {(isUnknown || isNeedsReview) && (
          <button
            onClick={lookupPRO}
            disabled={lookingUp}
            className="text-cyan-400 hover:text-cyan-300 bg-cyan-400/10 hover:bg-cyan-400/20 text-[10px] font-bold px-2 py-0.5 rounded-md transition flex items-center gap-1"
            title={`Search web for ${artist}'s PRO`}
            data-testid={`pro-lookup-${artist}`}
          >
            {lookingUp ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
            {lookingUp ? "" : "Lookup"}
          </button>
        )}
        {lookupResult && !lookingUp && (
          <span className={`text-[9px] font-medium ${lookupResult.source === "Auto-detected" ? "text-green-400" : "text-yellow-400"}`}>
            {lookupResult.source === "Auto-detected" ? `Found: ${lookupResult.pro_names?.join("+")}` : "No match"}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white/5 border border-purple-500/30 rounded-xl p-3 space-y-2 min-w-[180px]" data-testid={`pro-editor-${artist}`}>
      <p className="text-white/40 text-[10px] uppercase tracking-wider font-bold">Select PRO(s)</p>
      <div className="space-y-1.5">
        {pros.map(p => {
          const c = PRO_COLORS[p];
          const checked = selected.includes(p);
          return (
            <label key={p} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg cursor-pointer transition ${checked ? `${c.bg} border ${c.border}` : "hover:bg-white/5 border border-transparent"}`}>
              <input type="checkbox" checked={checked} onChange={() => toggle(p)} className="w-3.5 h-3.5 rounded accent-purple-500" />
              <span className={`text-xs font-medium ${checked ? c.text : "text-white/50"}`}>{p}</span>
            </label>
          );
        })}
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={save} disabled={saving}
          className="flex-1 bg-green-500/20 hover:bg-green-500/30 text-green-400 text-xs font-bold py-1.5 rounded-lg transition"
          data-testid={`pro-save-${artist}`}>
          {saving ? "Saving..." : "Save"}
        </button>
        <button onClick={() => setEditing(false)}
          className="bg-white/5 hover:bg-white/10 text-white/40 text-xs py-1.5 px-3 rounded-lg transition">
          Cancel
        </button>
      </div>
    </div>
  );
};

export const AnalyticsTab = ({ currentUser }) => {
  const [recentAirplays, setRecentAirplays] = useState([]);
  const [airplaySummary, setAirplaySummary] = useState([]);
  const [airplayLoading, setAirplayLoading] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [trackLog, setTrackLog] = useState([]);

  // PRO state
  const [proAirplay, setProAirplay] = useState([]);
  const [proSummary, setProSummary] = useState({});
  const [proLoading, setProLoading] = useState(false);
  const [showProReport, setShowProReport] = useState(false);
  const [proFilter, setProFilter] = useState(null); // null = all, "ASCAP" = filter
  const [bulkScanning, setBulkScanning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const authToken = localStorage.getItem("token");

  const fetchAirplay = useCallback(async () => {
    setAirplayLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${BACKEND_URL}/api/airplay/recent?limit=50`),
        fetch(`${BACKEND_URL}/api/airplay/summary`),
      ]);
      setRecentAirplays(await r1.json());
      setAirplaySummary(await r2.json());
    } catch (e) { console.error(e); }
    finally { setAirplayLoading(false); }
  }, []);

  const fetchPRO = useCallback(async () => {
    if (!authToken) return;
    setProLoading(true);
    try {
      const headers = { Authorization: `Bearer ${authToken}` };
      const [r1, r2] = await Promise.all([
        fetch(`${BACKEND_URL}/api/pro/airplay?limit=100`, { headers }),
        fetch(`${BACKEND_URL}/api/pro/summary`, { headers }),
      ]);
      setProAirplay(await r1.json());
      setProSummary(await r2.json());
    } catch (e) { console.error(e); }
    finally { setProLoading(false); }
  }, [authToken]);

  useEffect(() => {
    if (currentUser?.is_admin) {
      fetchAirplay();
      fetchPRO();
    }
  }, [currentUser, fetchAirplay, fetchPRO]);

  const viewTrackLog = async (artist, title) => {
    setSelectedTrack({ artist, title });
    try {
      const res = await fetch(`${BACKEND_URL}/api/airplay/track?artist=${encodeURIComponent(artist)}&title=${encodeURIComponent(title)}`);
      setTrackLog(await res.json());
    } catch (e) { setTrackLog([]); }
  };

  const exportCSV = (proName) => {
    let url = `${BACKEND_URL}/api/pro/export?token=${authToken}`;
    if (proName) url += `&pro=${encodeURIComponent(proName)}`;
    window.open(url, '_blank');
  };

  const scanAllUnknown = async () => {
    setBulkScanning(true);
    setBulkResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/pro/lookup/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ limit: 20 }),
      });
      const data = await res.json();
      setBulkResult(data);
      // Refresh PRO data after scan
      fetchPRO();
    } catch (e) { console.error(e); }
    finally { setBulkScanning(false); }
  };

  return (
  <div className="space-y-6">

    {/* PRO Tracking — admin only */}
    {currentUser?.is_admin && (
      <>
        {/* PRO Summary Cards */}
        {!proLoading && proSummary.total > 0 && (
          <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-testid="pro-summary-section">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-400" /> PRO Tracking
                </h3>
                <p className="text-white/40 text-sm mt-1">Performance Rights Organization breakdown by airplay</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setShowProReport(!showProReport)}
                  className="text-purple-400 hover:text-purple-300 text-xs font-medium bg-purple-400/10 hover:bg-purple-400/20 px-3 py-1.5 rounded-lg transition"
                  data-testid="toggle-pro-report"
                >
                  {showProReport ? "Hide Report" : "PRO Report"}
                </button>
                {proFilter && (
                  <button
                    onClick={() => exportCSV(proFilter)}
                    className="text-yellow-400 hover:text-yellow-300 text-xs font-medium bg-yellow-400/10 hover:bg-yellow-400/20 px-3 py-1.5 rounded-lg transition flex items-center gap-1"
                    data-testid="pro-export-filtered-btn"
                  >
                    <Download className="w-3 h-3" /> {proFilter} Report
                  </button>
                )}
                <button
                  onClick={() => exportCSV(null)}
                  className="text-green-400 hover:text-green-300 text-xs font-medium bg-green-400/10 hover:bg-green-400/20 px-3 py-1.5 rounded-lg transition flex items-center gap-1"
                  data-testid="pro-export-btn"
                >
                  <Download className="w-3 h-3" /> Export All
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 divide-x divide-white/5">
              {[
                { label: "Total", key: null, value: proSummary.total, color: "text-white" },
                { label: "ASCAP", key: "ASCAP", value: proSummary.ASCAP, color: "text-blue-400" },
                { label: "BMI", key: "BMI", value: proSummary.BMI, color: "text-green-400" },
                { label: "SESAC", key: "SESAC", value: proSummary.SESAC, color: "text-orange-400" },
                { label: "SoundEx", key: "SoundExchange", value: proSummary.SoundExchange, color: "text-purple-400" },
                { label: "Unknown", key: "Unknown", value: proSummary.Unknown, color: "text-red-400" },
              ].map(s => (
                <button
                  key={s.label}
                  onClick={() => setProFilter(proFilter === s.key ? null : s.key)}
                  className={`p-4 text-center transition cursor-pointer ${
                    proFilter === s.key ? "bg-white/[0.06] ring-1 ring-inset ring-purple-500/40" : "hover:bg-white/[0.03]"
                  }`}
                  data-testid={`pro-card-${s.label.toLowerCase()}`}
                >
                  <p className={`text-2xl font-black ${s.color}`}>{s.value || 0}</p>
                  <p className={`text-xs mt-1 ${proFilter === s.key ? "text-white/70 font-medium" : "text-white/40"}`}>{s.label}</p>
                  {proFilter === s.key && <p className="text-[9px] text-purple-400 mt-1 font-bold uppercase tracking-wider">Filtered</p>}
                </button>
              ))}
            </div>
            {/* Quick PRO Export Buttons */}
            <div className="p-4 border-t border-white/5 flex flex-wrap gap-2 items-center">
              <span className="text-white/30 text-xs font-medium mr-1">Export:</span>
              {["ASCAP", "BMI", "SESAC", "SoundExchange"].map(proName => (
                <button
                  key={proName}
                  onClick={() => exportCSV(proName)}
                  className={`text-xs font-medium px-3 py-1.5 rounded-lg transition flex items-center gap-1 ${
                    (PRO_COLORS[proName]?.bg || "bg-white/5") + " " +
                    (PRO_COLORS[proName]?.text || "text-white/50") + " " +
                    (PRO_COLORS[proName]?.border ? "border " + PRO_COLORS[proName].border : "")
                  } hover:opacity-80`}
                  data-testid={`pro-quick-export-${proName.toLowerCase()}`}
                >
                  <Download className="w-3 h-3" /> {proName}
                </button>
              ))}
              <button
                onClick={() => exportCSV(null)}
                className="text-xs font-medium px-3 py-1.5 rounded-lg transition flex items-center gap-1 bg-white/5 text-white/50 border border-white/10 hover:bg-white/10"
                data-testid="pro-quick-export-all"
              >
                <Download className="w-3 h-3" /> All PROs
              </button>
              <span className="text-white/10 mx-1">|</span>
              <button
                onClick={scanAllUnknown}
                disabled={bulkScanning}
                className="text-xs font-medium px-3 py-1.5 rounded-lg transition flex items-center gap-1 bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-500/20 disabled:opacity-50"
                data-testid="pro-scan-all-unknown"
              >
                {bulkScanning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                {bulkScanning ? "Scanning..." : "Scan All Unknown"}
              </button>
            </div>
            {/* Bulk Scan Result Banner */}
            {bulkResult && (
              <div className="mx-4 mb-4 p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-xl flex items-center justify-between" data-testid="bulk-scan-result">
                <div className="flex items-center gap-3">
                  <Search className="w-4 h-4 text-cyan-400" />
                  <div>
                    <p className="text-white text-xs font-medium">
                      Scanned {bulkResult.scanned} artist{bulkResult.scanned !== 1 ? "s" : ""}
                    </p>
                    <p className="text-white/40 text-[11px]">
                      {bulkResult.auto_detected} auto-detected · {bulkResult.needs_review} needs review{bulkResult.errors > 0 ? ` · ${bulkResult.errors} errors` : ""}
                    </p>
                  </div>
                </div>
                <button onClick={() => setBulkResult(null)} className="text-white/30 hover:text-white"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
          </div>
        )}

        {/* PRO Report — Detailed Table */}
        {showProReport && proAirplay.length > 0 && (() => {
          const filteredProAirplay = proFilter
            ? proAirplay.filter(t => (Array.isArray(t.pro) ? t.pro : [t.pro]).includes(proFilter))
            : proAirplay;
          return (
          <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-testid="pro-report-section">
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <div>
                <h3 className="text-white font-bold flex items-center gap-2">
                  PRO Report {proFilter ? `— ${proFilter}` : "— All Tracked Plays"}
                  {proFilter && (
                    <span className="text-xs font-medium text-purple-400 bg-purple-400/10 px-2.5 py-1 rounded-full flex items-center gap-1.5">
                      {filteredProAirplay.length} tracks
                      <button onClick={() => setProFilter(null)} className="text-white/40 hover:text-white ml-1"><X className="w-3 h-3" /></button>
                    </span>
                  )}
                </h3>
                <p className="text-white/40 text-xs mt-1">Click the edit icon to manually assign or correct a PRO</p>
              </div>
              {proFilter && (
                <button
                  onClick={() => exportCSV(proFilter)}
                  className="text-yellow-400 hover:text-yellow-300 text-xs font-medium bg-yellow-400/10 hover:bg-yellow-400/20 px-3 py-1.5 rounded-lg transition flex items-center gap-1"
                  data-testid="pro-report-export-filtered"
                >
                  <Download className="w-3 h-3" /> Export {proFilter} Report
                </button>
              )}
            </div>
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm" data-testid="pro-report-table">
                <thead className="sticky top-0 bg-[#0f0f0f] z-10">
                  <tr className="border-b border-white/10 text-white/40 text-xs uppercase tracking-wider">
                    <th className="text-left p-4">Artist</th>
                    <th className="text-left p-4">Track</th>
                    <th className="text-center p-4">Plays</th>
                    <th className="text-center p-4">Listeners</th>
                    <th className="text-center p-4">PRO</th>
                    <th className="text-left p-4">Source</th>
                    <th className="text-left p-4">Stations</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProAirplay.map((track, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`pro-row-${i}`}>
                      <td className="p-4 text-white font-medium">{track.artist}</td>
                      <td className="p-4 text-white/70">{track.title}</td>
                      <td className="p-4 text-center text-green-400 font-bold">{track.total_plays}</td>
                      <td className="p-4 text-center text-white/50">{track.total_listeners}</td>
                      <td className="p-4 text-center">
                        <PROInlineEditor
                          artist={track.artist}
                          currentPro={track.pro}
                          authToken={authToken}
                          onSaved={fetchPRO}
                          proSource={track.pro_source}
                        />
                      </td>
                      <td className="p-4 text-white/30 text-xs">
                        <span className={track.pro_source === "Auto-detected" ? "text-cyan-400/60" : track.pro_source === "Needs Review" ? "text-yellow-400/60" : ""}>
                          {track.pro_source}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {track.stations.map((s, j) => (
                            <span key={j} className="text-xs px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-400">{s}</span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          );
        })()}

        {/* Listener Map */}
        <ListenerMap />

        {/* Listener Heatmap & Trend */}
        <ListenerHeatmap authToken={authToken} />

        {/* Airplay Summary Table — now with PRO column */}
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-testid="airplay-analytics-section">
          <div className="p-6 border-b border-white/10 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Activity className="w-5 h-5 text-green-400" /> Airplay Analytics
                {proFilter && (
                  <span className="text-xs font-medium text-purple-400 bg-purple-400/10 px-2.5 py-1 rounded-full flex items-center gap-1.5">
                    Filtered: {proFilter}
                    <button onClick={() => setProFilter(null)} className="text-white/40 hover:text-white ml-1"><X className="w-3 h-3" /></button>
                  </span>
                )}
              </h3>
              <p className="text-white/40 text-sm mt-1">Tracks matched against approved submissions on your stations. Scanner runs every 2 minutes.</p>
            </div>
            <button onClick={() => { fetchAirplay(); fetchPRO(); }} className="text-purple-400 hover:text-purple-300 text-sm font-medium" data-testid="airplay-refresh">Refresh</button>
          </div>
          {airplayLoading ? (
            <div className="p-12 text-center text-white/40">Loading airplay data...</div>
          ) : airplaySummary.length === 0 ? (
            <div className="p-12 text-center text-white/40">
              <Activity className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium text-white/50">No airplay data yet</p>
              <p className="text-sm mt-1">The scanner will log plays as approved submissions are detected on your stations.</p>
            </div>
          ) : (() => {
            // Apply PRO filter to airplay summary
            const filteredAirplay = proFilter
              ? airplaySummary.filter(track => {
                  const proMatch = proAirplay.find(p => p.artist === track.artist && p.title === track.title);
                  const pros = proMatch ? (Array.isArray(proMatch.pro) ? proMatch.pro : [proMatch.pro]) : ["Unknown"];
                  return pros.includes(proFilter);
                })
              : airplaySummary;
            return filteredAirplay.length === 0 ? (
              <div className="p-12 text-center text-white/40">
                <p className="font-medium text-white/50">No {proFilter} tracks found</p>
                <button onClick={() => setProFilter(null)} className="text-purple-400 text-sm mt-2 hover:underline">Clear filter</button>
              </div>
            ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="airplay-summary-table">
                <thead>
                  <tr className="border-b border-white/10 text-white/40 text-xs uppercase tracking-wider">
                    <th className="text-left p-4">Track</th>
                    <th className="text-center p-4">PRO</th>
                    <th className="text-left p-4">Stations</th>
                    <th className="text-center p-4">Plays</th>
                    <th className="text-center p-4">Listeners</th>
                    <th className="text-left p-4">Last Played</th>
                    <th className="text-center p-4">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAirplay.map((track, i) => {
                    const proMatch = proAirplay.find(p => p.artist === track.artist && p.title === track.title);
                    return (
                      <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`airplay-row-${i}`}>
                        <td className="p-4">
                          <p className="text-white font-medium">{track.title}</p>
                          <p className="text-white/40 text-xs">{track.artist}</p>
                        </td>
                        <td className="p-4 text-center">
                          <PROInlineEditor
                            artist={track.artist}
                            currentPro={proMatch?.pro || "Unknown"}
                            authToken={authToken}
                            onSaved={fetchPRO}
                            proSource={proMatch?.pro_source || "Not Matched"}
                          />
                        </td>
                        <td className="p-4">
                          <div className="flex flex-wrap gap-1">
                            {track.stations.map((s, j) => (
                              <span key={j} className="text-xs px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-400">{s}</span>
                            ))}
                          </div>
                        </td>
                        <td className="p-4 text-center text-green-400 font-bold">{track.total_plays}</td>
                        <td className="p-4 text-center text-white/60">{track.total_listeners}</td>
                        <td className="p-4 text-white/40 text-xs">{track.last_played ? new Date(track.last_played * 1000).toLocaleString() : '-'}</td>
                        <td className="p-4 text-center">
                          <button onClick={() => viewTrackLog(track.artist, track.title)} className="text-purple-400 hover:text-purple-300 text-xs font-medium underline" data-testid={`airplay-detail-${i}`}>View Log</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ); })()}
        </div>
        {selectedTrack && (
          <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden" data-testid="airplay-detail-panel">
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <div>
                <h3 className="text-white font-bold">{selectedTrack.title}</h3>
                <p className="text-white/40 text-sm">{selectedTrack.artist} — Play History</p>
              </div>
              <button onClick={() => setSelectedTrack(null)} className="text-white/40 hover:text-white" data-testid="airplay-detail-close"><X className="w-5 h-5" /></button>
            </div>
            {trackLog.length === 0 ? (
              <div className="p-8 text-center text-white/40 text-sm">No play logs found.</div>
            ) : (
              <div className="max-h-80 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-[#0f0f0f]">
                    <tr className="border-b border-white/10 text-white/40 text-xs uppercase tracking-wider">
                      <th className="text-left p-3">Date/Time</th>
                      <th className="text-left p-3">Station</th>
                      <th className="text-left p-3">DJ / Source</th>
                      <th className="text-left p-3">Playlist</th>
                      <th className="text-center p-3">Listeners</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trackLog.map((log, i) => (
                      <tr key={i} className="border-b border-white/5">
                        <td className="p-3 text-white/50 text-xs">{new Date(log.played_at * 1000).toLocaleString()}</td>
                        <td className="p-3"><span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400">{log.station_name}</span></td>
                        <td className="p-3 text-white/60">{log.streamer || 'AutoDJ'}</td>
                        <td className="p-3 text-white/40 text-xs">{log.playlist || '-'}</td>
                        <td className="p-3 text-center text-white/50">{log.listeners}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Recent Feed */}
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="p-5 border-b border-white/10">
            <h3 className="text-white font-bold">Recent Airplay Feed</h3>
            <p className="text-white/40 text-xs mt-1">Last 50 detected plays across all stations</p>
          </div>
          {recentAirplays.length === 0 ? (
            <div className="p-8 text-center text-white/40 text-sm">No recent airplays detected yet.</div>
          ) : (
            <div className="max-h-96 overflow-y-auto divide-y divide-white/5">
              {recentAirplays.map((log, i) => (
                <div key={i} className="px-5 py-3 flex items-center gap-4 hover:bg-white/[0.02]" data-testid={`recent-airplay-${i}`}>
                  <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                    <Radio className="w-4 h-4 text-green-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{log.artist} — {log.title}</p>
                    <p className="text-white/40 text-xs">{log.station_name} · {log.streamer || 'AutoDJ'} · {log.playlist || 'N/A'} · {log.listeners} listeners</p>
                  </div>
                  <span className="text-white/30 text-xs flex-shrink-0">{new Date(log.played_at * 1000).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </>
    )}
  </div>
  );
};
